import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../css/PartnerDashboard.css';
import { API_BASE } from '../config';

const PartnerDashboard = ({ isAdminView = false, overridePartnerId = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    const fetchFullProfile = async () => {
      const sessionData = localStorage.getItem('partnerProfile');
      if (!sessionData && !overridePartnerId) {
        navigate('/login');
        return;
      }

      const localUser = sessionData ? JSON.parse(sessionData) : {};
      const partnerId = overridePartnerId || localUser.id || localUser._id;

      if (!partnerId) {
        setFetchingData(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/partners/profile/${partnerId}`);
        if (response.data && response.data.success) {
          const dbData = response.data.data;
          // Flattening structural database elements safely
          setFormData({
            ...dbData,
            accountNumber: dbData.PartnerProfile?.accountNumber || dbData.accountNumber || ''
          });
        } else {
          setFormData({
            ...localUser,
            accountNumber: localUser.PartnerProfile?.accountNumber || localUser.accountNumber || ''
          });
        }
      } catch (err) {
        console.error("Error fetching full profile:", err);
        setFormData({
          ...localUser,
          accountNumber: localUser.PartnerProfile?.accountNumber || localUser.accountNumber || ''
        });
      } finally {
        setFetchingData(false);
      }
    };

    fetchFullProfile();
  }, [navigate]);

  if (fetchingData) return <div className="loading-screen">Verifying Terminal Credentials... 🔄</div>;
  if (!formData) return <div className="loading-screen">No Profile Data Found.</div>;

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setFormData({ ...formData, profilePic: reader.result });
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await axios.put(`${API_BASE}/api/partners/update-profile`, formData);
      if (response.data.success) {
        const dbReturn = response.data.profile;
        const normalizedData = {
          ...dbReturn,
          accountNumber: dbReturn.PartnerProfile?.accountNumber || dbReturn.accountNumber || ''
        };
        localStorage.setItem('partnerProfile', JSON.stringify(normalizedData));
        setFormData(normalizedData);
        setIsEditing(false);
        setMessage({ type: 'success', text: '🎉 Profile database updated completely!' });
      }
    } catch (err) {
      setMessage({ type: 'danger', text: err.response?.data?.message || 'Failed to sync updates.' });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partnerProfile');
    navigate('/login');
  };

  return (
    <div className="dashboard-master-wrapper" style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div>
        {/* HEADER BANNER */}
        <div className="dashboard-top-card">
          <div className="header-flex">
            <h2>🎉 Welcome Back, {formData.contactName || 'Partner'}!</h2>
            <button className="btn-logout" onClick={handleLogout}>Logout 🚪</button>
          </div>
          <div className="meta-grid">
            <div className="meta-item">
              <span>ENTERPRISE TITLE</span>
              <p>{formData.companyName}</p>
            </div>
            <div className="meta-item">
              <span>UNIQUE USERNAME</span>
              <p className="username-badge">{formData.username}</p>
            </div>
            <div className="meta-item">
              <span>ROLE AUTHORITY</span>
              <p className="role-badge">{formData.roleType} {formData.subRoleType && formData.subRoleType !== 'None' ? `(${formData.subRoleType})` : ''}</p>
            </div>
          </div>
        </div>

        {/* ========================================================
            📈 PERFORMANCE HUB STATS (DYNAMIALLY GRIDDED) 
           ======================================================== */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px', margin: '25px 0' }}>
          
          {/* 1. VENDOR DATA CARDS */}
          {formData.roleType === 'Vendor' && (
            <>
              <div onClick={()=>navigate('/uploadproduct')} style={{ background: 'linear-gradient(135deg, #1e3c72, #2a5298)', padding: '20px', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <span style={{ fontSize: '0.85rem', opacity: 0.8, letterSpacing: '1px' }}>VENDOR PERFORMANCE</span>
                <h3 style={{ margin: '5px 0 15px 0', fontSize: '1.2rem', fontWeight: '500' }}>📦 Products Supplied to Us</h3>
                <h2 style={{ fontSize: '2.5rem', margin: 0, color: '#2ecc71' }}>{formData.totalProductsSupplied || 0} <span style={{ fontSize: '1rem', color: '#fff' }}>Items</span></h2>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #1e3c72, #2a5298)', padding: '20px', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <span style={{ fontSize: '0.85rem', opacity: 0.8, letterSpacing: '1px' }}>BUSINESS VOLUME</span>
                <h3 style={{ margin: '5px 0 15px 0', fontSize: '1.2rem', fontWeight: '500' }}>💼 Total Sales Generated</h3>
                <h2 style={{ fontSize: '2.5rem', margin: 0, color: '#3498db' }}>₹{formData.totalSales || 0}</h2>
              </div>
            </>
          )}

          {/* 2. BUYER DATA CARDS */}
          {formData.roleType === 'Buyer' && (
            <>
              <div style={{ background: 'linear-gradient(135deg, #11998e, #38ef7d)', padding: '20px', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <span style={{ fontSize: '0.85rem', opacity: 0.8, letterSpacing: '1px' }}>PROCUREMENT TRACKER</span>
                <h3 style={{ margin: '5px 0 15px 0', fontSize: '1.2rem', fontWeight: '500' }}>🛒 Products Purchased</h3>
                <h2 style={{ fontSize: '2.5rem', margin: 0, color: '#fff' }}>{formData.totalProductsBought || 0} <span style={{ fontSize: '1rem' }}>Units</span></h2>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #11998e, #38ef7d)', padding: '20px', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <span style={{ fontSize: '0.85rem', opacity: 0.8, letterSpacing: '1px' }}>FINANCIAL METRICS</span>
                <h3 style={{ margin: '5px 0 15px 0', fontSize: '1.2rem', fontWeight: '500' }}>💳 Total Procurement Spend</h3>
                <h2 style={{ fontSize: '2.5rem', margin: 0, color: '#fff' }}>₹{formData.totalSales || 0}</h2>
              </div>
            </>
          )}

          {/* 3. SALES AGENT SPECIFIC DATA CARDS */}
          {formData.subRoleType === 'Sales Agent' && (
            <>
              <div style={{ background: 'linear-gradient(135deg, #8e2de2, #4a00e0)', padding: '20px', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <span style={{ fontSize: '0.85rem', opacity: 0.8, letterSpacing: '1px' }}>AGENT TARGET TRACKER</span>
                <h3 style={{ margin: '5px 0 15px 0', fontSize: '1.2rem', fontWeight: '500' }}>📈 Managed Products Sold</h3>
                <h2 style={{ fontSize: '2.5rem', margin: 0, color: '#f1c40f' }}>{formData.totalProductsSold || 0} <span style={{ fontSize: '1rem', color: '#fff' }}>Orders</span></h2>
              </div>
              <div style={{ background: 'linear-gradient(135deg, #8e2de2, #4a00e0)', padding: '20px', borderRadius: '12px', color: '#fff', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}>
                <span style={{ fontSize: '0.85rem', opacity: 0.8, letterSpacing: '1px' }}>COMMISSION REVENUE</span>
                <h3 style={{ margin: '5px 0 15px 0', fontSize: '1.2rem', fontWeight: '500' }}>💰 Earned Commission Payout</h3>
                <h2 style={{ fontSize: '2.5rem', margin: 0, color: '#2ecc71' }}>₹{formData.totalCommission || 0}</h2>
              </div>
            </>
          )}
        </div>

        {/* MAIN DATA FORM PROFILE CARD */}
        <div className="profile-content-card">
          <div className="card-inner-header">
            <h3>👤 Profile & Financial Records</h3>
            <button 
              type="button" 
              className={`btn-mode-toggle ${isEditing ? 'btn-cancel' : 'btn-edit'}`} 
              onClick={() => { setIsEditing(!isEditing); setMessage({ type: '', text: '' }); }}
            >
              {isEditing ? 'Cancel Editing ❌' : 'Edit Details 📝'}
            </button>
          </div>

          {message.text && <div className={`alert-banner ${message.type}`}>{message.text}</div>}

          <form onSubmit={handleSaveProfile} className="profile-dynamic-form">
            <div className="profile-grid-layout">
              
              <div className="avatar-upload-section">
                <div className="avatar-preview-box">
                  {formData.profilePic ? (
                    <img src={formData.profilePic} alt="Avatar" className="user-avatar" />
                  ) : (
                    <div className="avatar-placeholder">{formData.companyName?.charAt(0).toUpperCase()}</div>
                  )}
                </div>
                {isEditing && (
                  <div className="file-input-wrapper">
                    <label htmlFor="avatar-file" className="btn-file-upload">Change Avatar 📸</label>
                    <input type="file" id="avatar-file" accept="image/*" onChange={handleProfilePicChange} style={{ display: 'none' }} />
                  </div>
                )}
              </div>

              <div className="fields-input-section">
                <div className="form-row">
                  <div className="form-field">
                    <label>Full Representative Name</label>
                    <input 
                      type="text" 
                      name="contactName" 
                      value={formData.contactName || ''} 
                      onChange={handleInputChange} 
                      disabled={!isEditing}
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label>Company/Enterprise Name</label>
                    <input 
                      type="text" 
                      name="companyName" 
                      value={formData.companyName || ''} 
                      onChange={handleInputChange} 
                      disabled={!isEditing}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label>Mobile / Contact Number</label>
                    <input type="text" name="mobileNumber" value={formData.mobileNumber || ''} onChange={handleInputChange} disabled={!isEditing} />
                  </div>
                  <div className="form-field">
                    <label>Designation</label>
                    <input type="text" name="designation" value={formData.designation || ''} onChange={handleInputChange} disabled={!isEditing} />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field full-width">
                    <label>Bank Account Number / Clearance ID</label>
                    <input 
                      type="text" 
                      name="accountNumber" 
                      value={formData.accountNumber || ''} 
                      onChange={handleInputChange} 
                      disabled={!isEditing}
                    />
                  </div>
                </div>

                {isEditing && (
                  <button type="submit" className="btn-save-submit" disabled={loading}>
                    {loading ? 'Saving to Database...' : 'Commit Changes & Save ✨'}
                  </button>
                )}
              </div>

            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PartnerDashboard;